import { useDatabase } from '../composables/database'
import { Directory, Filesystem } from '@capacitor/filesystem'
import { useFilesystem } from '../composables/filesystem'
import { Capacitor } from '@capacitor/core'

// import { InventoryBalance } from '../store/useInventory'
export interface ImageData {
  name: string
  id: string
}
export interface DownloadImageData extends ImageData {
  link: string
}
type AllPartsModelReturn = {
  /**Return all parts on device */
  getAll: () => Promise<any[]>
  /**Writes parts to device */
  writeParts: (data: any[]) => Promise<void>
  /**Deletes all parts from device */
  clearParts: () => Promise<void>
  /**Downloads a part image given a link. Returns the filesystem location. Capacitor only. */
  downloadPartImage: (data: DownloadImageData, skip_error?: boolean) => Promise<string>
  /**Checks if a image is on device. Capacitor only. */
  imageOnDevice: (data: DownloadImageData) => Promise<boolean>
  /**Returns path for specified image. Capacitor only. */
  getImagePath: (data: DownloadImageData) => Promise<string>
}
export const AllPartsModel = (): AllPartsModelReturn => {
  const IMAGE_FOLDER = 'FHS/part_images'
  const DIRECTORY = Directory.Data
  const { run, query } = useDatabase()

  const getAll = async () => {
    const sql = `
        SELECT * FROM parts WHERE id = 1 ORDER BY id DESC LIMIT 1;
        `
    return await query(sql)
      .catch((error) => console.log(`Error grabbing all parts: ${error}`))
      .then((parts) => {
        if (parts && parts.length) {
          return JSON.parse(parts[0]['parts_data'])
        } else {
          return []
        }
      })
  }

  const writeParts = async (data: any[]) => {
    const sql = `
        INSERT OR REPLACE INTO parts ('id', 'parts_data')
        VALUES (1, ?);
    `
    await run(sql, [JSON.stringify(data)]).catch((error) =>
      console.log(`Error writing parts to DB: ${error}`)
    )
  }
  //Capacitor only
  const makePartImageDir = async () => {
    await Filesystem.mkdir({
      path: IMAGE_FOLDER,
      directory: DIRECTORY,
      recursive: true,
    }).catch((error) => {
      if (error != 'Error: Directory exists') console.log(`Error creating directory: ${error}`)
    })
  }
  const imageOnDevice = async (data: ImageData) => {
    await makePartImageDir()
    const fileName = `${data.id}-${data.name}`
    const files = await Filesystem.readdir({
      path: IMAGE_FOLDER,
      directory: DIRECTORY,
    })
    return files.files.includes(fileName)
  }
  //helper
  const attachmentPath = (data: ImageData) => {
    return `${IMAGE_FOLDER}/${data.id}-${data.name}`
  }

  const downloadPartImage = async (data: DownloadImageData, skip_error = false) => {
    await makePartImageDir()
    //delete file if already there
    const fileExists = await imageOnDevice(data)
    if (fileExists) {
      await deleteImage(data)
    }
    //download file
    await useFilesystem().fetchFile(
      {
        filePath: attachmentPath(data),
        url: data.link,
      },
      skip_error
    )

    return useFilesystem().getURI({
      path: attachmentPath(data),
      directory: DIRECTORY,
    })
  }

  const deleteImage = async (data: ImageData) => {
    await Filesystem.deleteFile({
      path: attachmentPath(data),
      directory: DIRECTORY,
    })
  }
  const clearParts = async () => {
    const sql = 'DELETE FROM parts;'
    await run(sql, []).catch((error) => console.log(`Error deleting parts: ${error}`))
    //nuke images
    if (Capacitor.isNativePlatform()) {
      await makePartImageDir() //needed in case the parts dir wasn't created
      await useFilesystem().removeDir({ path: IMAGE_FOLDER, directory: DIRECTORY })
    }
  }

  const getImagePath = async (data: DownloadImageData) => {
    return await useFilesystem().getURI({
      path: attachmentPath(data),
      directory: DIRECTORY,
    })
  }

  return {
    getAll,
    writeParts,
    clearParts,
    downloadPartImage,
    imageOnDevice,
    getImagePath,
  }
}
